import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';

/**
 * SSO Handler Component
 * 
 * This component handles the SSO callback from WordPress.
 * It receives an SSO token in the URL, validates it with the backend,
 * and logs the user in if successful.
 */
function SSOHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleLogin } = useContext(AppContext);
  
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleSSO = async () => {
      const ssoToken = searchParams.get('token');

      if (!ssoToken) {
        setStatus('error');
        setError('No SSO token provided');
        return;
      }

      try {
        setStatus('processing');

        // Call the SSO endpoint
        const response = await fetch('/api/auth/sso', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ssoToken }),
          credentials: 'include', // Include cookies
        });

        const data = await response.json();

        if (response.ok && data.token) {
          // SSO successful - log the user in
          // The handleLogin function from AppContext expects a single object with all login data
          handleLogin({
            token: data.token,
            user: data.user,
            cemeteries: data.cemeteries || [],
            tenantPlan: data.tenantPlan,
            availableFeatures: data.availableFeatures
          });
          
          setStatus('success');
          
          // Redirect to dashboard after a brief moment
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1000);
        } else {
          setStatus('error');
          setError(data.message || 'SSO login failed');
        }
      } catch (err) {
        console.error('SSO error:', err);
        setStatus('error');
        setError('An error occurred during login. Please try again.');
      }
    };

    handleSSO();
  }, [searchParams, handleLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Signing you in...</h2>
            <p className="text-gray-600">Please wait while we verify your credentials.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Login Successful!</h2>
            <p className="text-gray-600">Redirecting you to your dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Login Failed</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
            >
              Go to Login Page
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default SSOHandler;
